import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { cn } from '@rocco/utils/cn';
import {
    PROJECT_BROWSE_VIEWS,
    type ProjectBrowseView,
} from '@modules-rocco/projects/config/project-browse-views';

interface DirectoryBrowseTabsProps {
    /** Tab to render as current. */
    activeView: ProjectBrowseView;
}

/**
 * Projects / Spaces / Styles browse nav (Figma 5762:114114). Each tab carries a
 * title and a sub-label naming the axis it browses, over a hairline rule; the
 * active tab is inked white and marked by a heavy bar sitting on that rule.
 *
 * Desktop sizes off the 1920 frame at the page's 16px rem base (Figma px ÷ 16).
 * There is no mobile frame for this block, so below md the tabs drop to the
 * mobile type scale and scroll horizontally rather than wrapping.
 */
export const DirectoryBrowseTabs = ({
    activeView,
}: DirectoryBrowseTabsProps) => {
    const location = useLocation();
    const navRef = useRef<HTMLElement>(null);

    // Switching tab lands the reader at the tab row rather than wherever the
    // previous view was scrolled to, so the three modes read as one control.
    // Only fires for tab clicks (the state flag) — a direct URL visit or a
    // Back that happens to land here keeps its own scroll position.
    useEffect(() => {
        const state = location.state as { fromBrowseTab?: boolean } | null;
        if (!state?.fromBrowseTab || !navRef.current) {
            return;
        }
        const reduceMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        navRef.current.scrollIntoView({
            block: 'start',
            behavior: reduceMotion ? 'auto' : 'smooth',
        });
    }, [location.key, location.state]);
    return (
        <nav
            ref={navRef}
            aria-label="Browse projects by"
            // Clears the fixed header so the row is not tucked under it.
            className="scroll-mt-[5.5rem] md:scroll-mt-[2rem]"
        >
            {/* The rule runs the full content width; the tabs sit on it. The
                row scrolls below md, where three 208px slots do not fit. */}
            <div className="relative flex [scrollbar-width:none] border-b border-white/10 max-md:gap-8 max-md:overflow-x-auto max-md:pb-[1.125rem] md:pb-[1.7375rem] [&::-webkit-scrollbar]:hidden">
                {PROJECT_BROWSE_VIEWS.map(({ view, path, label, subLabel }) => {
                    const isActive = view === activeView;
                    return (
                        <Link
                            key={view}
                            to={path}
                            state={{ fromBrowseTab: true }}
                            aria-current={isActive ? 'page' : undefined}
                            className={cn(
                                'relative flex shrink-0 flex-col md:w-[13rem]',
                                'gap-[0.375rem] md:gap-[0.703125rem]',
                            )}
                        >
                            <span
                                className={cn(
                                    'font-sans whitespace-nowrap',
                                    'text-[1.5rem] leading-[1.25] tracking-[-0.0375rem]',
                                    'md:text-[2.0625rem] md:tracking-[-0.0515625rem]',
                                    isActive
                                        ? 'font-medium text-white'
                                        : 'font-normal text-[#9a9a9a]',
                                )}
                            >
                                {label}
                            </span>
                            <span className="text-[0.8125rem] leading-[1.22] font-medium whitespace-nowrap text-[#9a9a9a] md:text-[0.9375rem]">
                                {subLabel}
                            </span>
                            {/* Active marker: a 6.5px bar whose bottom edge
                                meets the rule. Fixed width per the design — the
                                bar is the same size under whichever tab is
                                current, not fitted to the label. The offset
                                cancels the row's padding, which the link box
                                stops short of, so the bar lands on the rule
                                rather than under the sub-label. */}
                            {isActive ? (
                                <span
                                    aria-hidden="true"
                                    className="absolute -bottom-[1.125rem] left-0 h-[0.25rem] w-[6.5rem] bg-[#d9d9d9] md:-bottom-[1.7375rem] md:h-[0.4068rem] md:w-[10.4551rem]"
                                />
                            ) : null}
                        </Link>
                    );
                })}
            </div>
        </nav>
    );
};
